 
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

type Shop = {
   orgId: number; 
  orgName: string;
  address: string;
  city: string;
  area:string;
  mobile: string;
  lat: number | string;
  long: number | string; 
  categories: any[];
};

type Props = {
  shops: Shop[];
  isColorArea: boolean;
  setisColorArea: (data: boolean) => void;
};

/* -------------------------------------------------------------------------- */
/* Marker Colors                                                              */
/* -------------------------------------------------------------------------- */

const colors = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#22C55E",
  "#10B981",
  "#06B6D4",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#EC4899",
  "#F43F5E",
  "#14B8A6",
  "#84CC16",
];

/* -------------------------------------------------------------------------- */
/* Create Stable Area Colors                                                  */
/* -------------------------------------------------------------------------- */

const createCityColors = (shops: Shop[]) => {
  const cityColors: Record<string, string> = {};

  const cities = Array.from(
    new Set(
      shops
        .map((shop) => shop.area?.trim())
        .filter(Boolean)
    )
  );

  cities.forEach((city, index) => {
    cityColors[city] = colors[index % colors.length];
  });

  return cityColors;
};

/* -------------------------------------------------------------------------- */
/* Marker Component                                                           */
/* -------------------------------------------------------------------------- */

type ShopMarkerProps = {
  shop: Shop;
  color: string;
  onClick: (shop: Shop) => void;
};

const ShopMarker = memo(
  ({ shop, color, onClick }: ShopMarkerProps) => {
    /**
     * Position is created only when lat/long change.
     * This prevents creating 2500+ objects on every render.
     */
    const position = useMemo(
      () => ({
        lat: Number(shop.lat),
        lng: Number(shop.long),
      }),
      [shop.lat, shop.long]
    );

    const handleClick = useCallback(() => {
      onClick(shop);
    }, [onClick, shop]);

    return (
      <AdvancedMarker
        position={position}
        onClick={handleClick}
      >
 
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "2px",

              width: "28px",
              height: "28px",

              backgroundColor: color,

              border: "2px solid #ffffff",

              borderRadius:
                "50% 50% 50% 0",

              transform:
                "translateX(-50%) rotate(-45deg)",

              boxShadow:
                "0 2px 5px rgba(0,0,0,0.28)",
            }}
          />

          {/* Center */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "9px",

              width: "10px",
              height: "10px",

              backgroundColor: "#ffffff",

              borderRadius: "50%",

              transform: "translateX(-50%)",

              boxShadow:
                "0 1px 2px rgba(0,0,0,0.15)",
            }}
          /> 

        {/* <div
          style={{
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: color,
            border: "2px solid #fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
            cursor: "pointer",
          }}
        /> */}
           {/* <div
    style={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      background: color,
      border: "3px solid #fff",
      boxShadow: "0 2px 6px rgba(0,0,0,.25)",
    }}
  /> */}
      </AdvancedMarker>
    );
  },
  (prev, next) => {
    return (
      prev.shop.orgId === next.shop.orgId &&
      prev.shop.lat === next.shop.lat &&
      prev.shop.long === next.shop.long &&
      prev.color === next.color &&
      prev.onClick === next.onClick
    );
  }
);

ShopMarker.displayName = "ShopMarker";

/* -------------------------------------------------------------------------- */
/* Map Controller                                                             */
/* -------------------------------------------------------------------------- */

type MapControllerProps = {
  shops: Shop[];
};

const MapController = memo(
  ({ shops }: MapControllerProps) => {
    const map = useMap();

    /**
     * Prevent fitBounds from running repeatedly.
     */
    const previousKey = useRef("");

    useEffect(() => { 
      if (!map || shops.length === 0) {
        return;
      }

      /**
       * Create a stable key from the shop IDs.
       * fitBounds will only run when the actual shop collection changes.
       */
      const shopsKey = shops
        .map((shop) => shop.orgId)
        .join(",");

      if (previousKey.current === shopsKey) {
        return;
      }

      previousKey.current = shopsKey;

      const bounds =
        new window.google.maps.LatLngBounds();

      let validCount = 0;

      shops.forEach((shop) => {
        const lat = Number(shop.lat);
        const lng = Number(shop.long);

        if (
          Number.isFinite(lat) &&
          Number.isFinite(lng)
        ) {
          bounds.extend({ lat, lng });
          validCount++;
        }
      });

      if (validCount === 0) {
        return;
      }

      /**
       * Only fit bounds once after data changes.
       */
      map.fitBounds(bounds, 80);

      /**
       * For one location, use a fixed zoom.
       */
      if (validCount === 1) {
        window.google.maps.event.addListenerOnce(
          map,
          "idle",
          () => {
            map.setZoom(15);
          }
        );
      }
    }, [map, shops]);

    return null;
  }
);

MapController.displayName = "MapController";

/* -------------------------------------------------------------------------- */
/* Maps Component                                                             */
/* -------------------------------------------------------------------------- */

const Maps = ({
  shops,
  isColorArea,
}: Props) => {
  const [selectedShop, setSelectedShop] =
    useState<Shop | null>(null);

  /* ---------------------------------------------------------------------- */
  /* Valid Shops                                                            */
  /* ---------------------------------------------------------------------- */

  const validShops = useMemo(() => {
    return shops.filter((shop) => {
      const lat = Number(shop.lat);
      const lng = Number(shop.long);

      return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat !== 0 &&
        lng !== 0
      );
    });
  }, [shops]);

  /* ---------------------------------------------------------------------- */
  /* Area Colors                                                            */
  /* ---------------------------------------------------------------------- */

  const cityColors = useMemo(() => {
    if (!shops.length) {
      return {};
    }

    return createCityColors(shops);
  }, [shops]);

  /* ---------------------------------------------------------------------- */
  /* Default Center                                                         */
  /* ---------------------------------------------------------------------- */

  const center = useMemo(() => {
    if (validShops.length > 0) {
      return {
        lat: Number(validShops[0].lat),
        lng: Number(validShops[0].long),
      };
    }

    return {
      lat: 24.2669,
      lng: 45.1078,
    };
  }, [validShops]);

  /* ---------------------------------------------------------------------- */
  /* Marker Click                                                           */
  /* ---------------------------------------------------------------------- */

  const handleMarkerClick = useCallback(
    (shop: Shop) => {
      setSelectedShop(shop);
    },
    []
  );

  /* ---------------------------------------------------------------------- */
  /* Close InfoWindow                                                       */
  /* ---------------------------------------------------------------------- */

  const handleInfoWindowClose = useCallback(() => {
    setSelectedShop(null);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Selected Shop Position                                                 */
  /* ---------------------------------------------------------------------- */

  const selectedPosition = useMemo(() => {
    if (!selectedShop) {
      return null;
    }

    return {
      lat: Number(selectedShop.lat),
      lng: Number(selectedShop.long),
    };
  }, [
    selectedShop?.lat,
    selectedShop?.long,
  ]);

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <APIProvider
      apiKey={
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
      }
    >
      <Map
        defaultCenter={center}
        defaultZoom={6}
        mapId="YOUR_MAP_ID"
        style={{
          width: "100%",
          height: "800px",
        }}
        gestureHandling="greedy"
        disableDefaultUI={false}
      >
        {/* ---------------------------------------------------------------- */}
        {/* Map Controller                                                   */}
        {/* ---------------------------------------------------------------- */}

        <MapController shops={validShops} />

        {/* ---------------------------------------------------------------- */}
        {/* Markers                                                          */}
        {/* ---------------------------------------------------------------- */}

        {validShops.map((shop) => {
          const area =
            shop.area?.trim() || "Unknown";

          const markerColor = isColorArea
            ? cityColors[area] || "#64748B"
            : "#3B82F6";

          return (
            <ShopMarker
              key={shop.orgId}
              shop={shop}
              color={markerColor}
              onClick={handleMarkerClick}
            />
          );
        })}

        {/* ---------------------------------------------------------------- */}
        {/* Info Window                                                      */}
        {/* ---------------------------------------------------------------- */}

        {selectedShop && selectedPosition && (
          <InfoWindow
            position={selectedPosition}
            onCloseClick={handleInfoWindowClose}
          >
            <div
              className="p-4"
              style={{
                minWidth: "250px",
              }}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                   <h3 className="font-headline-md text-label-md text-primary m-0">
                   <span>الفرع:</span> {selectedShop.orgName}
                  </h3>

                  <p className="text-label-sm text-on-surface-variant mt-1">
                    <span>المدينة:</span>{selectedShop.city}
                  </p>
                   <p className="text-label-sm text-on-surface-variant mt-1">
                    <span>المنطقة:</span>{selectedShop.area}
                  </p>
                    <p className="text-label-sm text-on-surface-variant mt-1">
                  <span>الخدمة:</span> {selectedShop.categories}
                  </p>
                </div>

                <span
                  className="material-symbols-outlined"
                  style={{
                    color:
                      cityColors[
                        selectedShop.area?.trim()
                      ] || "#64748B",
                    fontSize: "20px",
                  }}
                >
                  verified
                </span>
              </div>

              <div className="flex items-center gap-1 text-label-sm text-on-surface-variant">
                <span className="material-symbols-outlined text-[14px]">
                  location_on
                </span>

                <span>
                  العنوان:{selectedShop.address}
                </span>
              </div>
            </div>
          </InfoWindow>
        )}
      </Map>
    </APIProvider>
  );
};

export default memo(Maps);








// import {
//   APIProvider,
//   Map,
//   AdvancedMarker,
//   InfoWindow,
//   useMap,
// } from "@vis.gl/react-google-maps";
// import { useEffect, useState } from "react";

// /* -------------------------------------------------------------------------- */
// /* Types                                                                      */
// /* -------------------------------------------------------------------------- */

// type Shop = {
//   branchId: number;
//   shopId: number;
//   branchName: string;
//   address: string;
//   city: string;
//   area: string;
//   mobile: string;
//   lat: number | string;
//   long: number | string;
//   imgSrc: string;
//   categories: any[];
// };

// type Props = {
//   shops: Shop[];
//   isColorArea: boolean;
//   setisColorArea: (data: boolean) => void;
// };

// /* -------------------------------------------------------------------------- */
// /* Marker Colors                                                              */
// /* -------------------------------------------------------------------------- */

// const colors = [
//   "#EF4444",
//   "#F97316",
//   "#F59E0B",
//   "#EAB308",
//   "#22C55E",
//   "#10B981",
//   "#06B6D4",
//   "#3B82F6",
//   "#6366F1",
//   "#8B5CF6",
//   "#A855F7",
//   "#EC4899",
//   "#F43F5E",
//   "#14B8A6",
//   "#84CC16",
// ];

// /* -------------------------------------------------------------------------- */
// /* Generate Color For Each Area                                               */
// /* -------------------------------------------------------------------------- */

// const createCityColors = (shops: Shop[]) => {
//   const cityColors: Record<string, string> = {};

//   const cities = [
//     ...new Set(
//       shops
//         .map((shop) => shop.area?.trim())
//         .filter(Boolean)
//     ),
//   ];

//   const availableColors = [...colors];

//   cities.forEach((city) => {
//     if (availableColors.length === 0) {
//       availableColors.push(...colors);
//     }

//     const randomIndex = Math.floor(
//       Math.random() * availableColors.length
//     );

//     cityColors[city] = availableColors[randomIndex];

//     availableColors.splice(randomIndex, 1);
//   });

//   return cityColors;
// };

// /* -------------------------------------------------------------------------- */
// /* Map Controller                                                             */
// /* -------------------------------------------------------------------------- */

// const MapController = ({ shops }: { shops: Shop[] }) => {
//   const map = useMap();

//   useEffect(() => {
//     if (!map || shops.length === 0) return;

//     const bounds = new window.google.maps.LatLngBounds();

//     shops.forEach((shop) => {
//       const lat = Number(shop.lat);
//       const lng = Number(shop.long);

//       if (Number.isFinite(lat) && Number.isFinite(lng)) {
//         bounds.extend({ lat, lng });
//       }
//     });

//     map.fitBounds(bounds, 100);

//     if (shops.length === 1) {
//       map.setZoom(15);
//     }
//   }, [map, shops]);

//   return null;
// };

// /* -------------------------------------------------------------------------- */
// /* Maps Component                                                             */
// /* -------------------------------------------------------------------------- */

// const Maps = ({
//   shops,
//   isColorArea,
// }: Props) => {
//   const [selectedShop, setSelectedShop] =
//     useState<Shop | null>(null);

//   const [cityColors, setCityColors] = useState<
//     Record<string, string>
//   >({});

//   /* -------------------------- Update City Colors ------------------------- */

//   useEffect(() => {
//     if (!shops.length) {
//       setCityColors({});
//       return;
//     }

//     setCityColors(createCityColors(shops));
//   }, [shops]);

//   /* -------------------------- Valid Locations ---------------------------- */

//   const validShops = shops.filter((shop) => {
//     const lat = Number(shop.lat);
//     const lng = Number(shop.long);

//     return (
//       Number.isFinite(lat) &&
//       Number.isFinite(lng) &&
//       lat !== 0 &&
//       lng !== 0
//     );
//   });

//   /* --------------------------- Default Center ---------------------------- */

//   const center = {
//     lat:
//       validShops.length > 0
//         ? Number(validShops[0].lat)
//         : 24.2669,
//     lng:
//       validShops.length > 0
//         ? Number(validShops[0].long)
//         : 45.1078,
//   };

//   /* ---------------------------------------------------------------------- */

//   return (
//     <APIProvider
//       apiKey={
//         process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
//       }
//     >
//       <Map
//         defaultCenter={center}
//         defaultZoom={6}
//         mapId="YOUR_MAP_ID"
//         style={{
//           width: "100%",
//           height: "800px",
//         }}
//       >
//         <MapController shops={validShops} />

//         {/* Markers */}

//         {validShops.map((shop) => {
//           const city =
//             shop.area?.trim() || "Unknown";

//           const markerColor = isColorArea
//             ? cityColors[city] || "#64748B"
//             : "#3B82F6";

//           return ( 
 
// <AdvancedMarker
// key={shop.branchId}
// position={{
// lat: Number(shop.lat),
// lng: Number(shop.long),
// }} 
// onClick={() => setSelectedShop(shop)}
// > 
//  <svg
//     width="32"
//     height="40"
//     viewBox="0 0 24 24"
//     fill={markerColor}
//   >
//     <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
//     <circle
//       cx="12"
//       cy="9"
//       r="3"
//       fill="white"
//     />
//   </svg>
// </AdvancedMarker>

// // {/* <AdvancedMarker
// //   key={shop.branchId}
// //   position={{
// //     lat: Number(shop.lat),
// //     lng: Number(shop.long),
// //   }}
// //   onClick={() => setSelectedShop(shop)}
// // >
// //   <div
// //     style={{
// //       width: 28,
// //       height: 28,
// //       borderRadius: "50%",
// //       background: markerColor,
// //       border: "3px solid #fff",
// //       boxShadow: "0 2px 6px rgba(0,0,0,.25)",
// //     }}
// //   />
// // </AdvancedMarker> */}


// //             <AdvancedMarker
// //   key={shop.branchId}
// //   position={{
// //     lat: Number(shop.lat),
// //     lng: Number(shop.long),
// //   }}
// //   onClick={() => setSelectedShop(shop)}
// // >
// //   <svg
// //     width="32"
// //     height="40"
// //     viewBox="0 0 24 24"
// //     fill={markerColor}
// //   >
// //     <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
// //     <circle
// //       cx="12"
// //       cy="9"
// //       r="3"
// //       fill="white"
// //     />
// //   </svg>
// // </AdvancedMarker>
//             // <AdvancedMarker
//             //   key={shop.branchId}
//             //   position={{
//             //     lat: Number(shop.lat),
//             //     lng: Number(shop.long),
//             //   }}
//             //   onClick={() => setSelectedShop(shop)}
//             // >
//             //   <div
//             //     style={{
//             //       width: "30px",
//             //       height: "30px",
//             //       borderRadius: "50% 50% 50% 0",
//             //       backgroundColor: markerColor,
//             //       transform: "rotate(-45deg)",
//             //       display: "flex",
//             //       alignItems: "center",
//             //       justifyContent: "center",
//             //       boxShadow:
//             //         "0 3px 8px rgba(0,0,0,0.3)",
//             //       border: "2px solid white",
//             //       cursor: "pointer",
//             //     }}
//             //   >
//             //     <span
//             //       className="material-symbols-outlined"
//             //       style={{
//             //         color: "white",
//             //         fontSize: "22px",
//             //         transform: "rotate(45deg)",
//             //       }}
//             //     >
//             //       location_on
//             //     </span>
//             //   </div>
//             // </AdvancedMarker>
//           );
//         })}

//         {/* Info Window */}

//         {selectedShop && (
//           <InfoWindow
//             position={{
//               lat: Number(selectedShop.lat),
//               lng: Number(selectedShop.long),
//             }}
//             onCloseClick={() =>
//               setSelectedShop(null)
//             }
//           >
//             <div
//               className="p-4"
//               style={{ minWidth: "250px" }}
//             >
//               <div className="flex justify-between items-start mb-2">
//                 <div>
//                   <h3 className="font-headline-md text-label-md text-primary m-0">
//                     {selectedShop.branchName}
//                   </h3>

//                   <p className="text-label-sm text-on-surface-variant mt-1">
//                     {selectedShop.city}
//                   </p>
//                 </div>

//                 <span
//                   className="material-symbols-outlined"
//                   style={{
//                     color:
//                       cityColors[
//                         selectedShop.city?.trim()
//                       ] || "#64748B",
//                     fontSize: "20px",
//                   }}
//                 >
//                   verified
//                 </span>
//               </div>

//               <div className="flex items-center gap-1 text-label-sm text-on-surface-variant">
//                 <span className="material-symbols-outlined text-[14px]">
//                   location_on
//                 </span>

//                 <span>
//                   {selectedShop.address}
//                 </span>
//               </div>
//             </div>
//           </InfoWindow>
//         )}
//       </Map>
//     </APIProvider>
//   );
// };

// export default Maps;
 


 



















// import ClusterMarkers from "../Components/General/ClusterMarkers";

 

// import {
//   APIProvider,
//   Map,
//   AdvancedMarker,
//   InfoWindow,
//   useMap,
// } from "@vis.gl/react-google-maps";

// import { useEffect, useState } from "react";

// type Shop = {
//   branchId: number;
//   shopId: number;
//   branchName: string;
//   address: string;
//   city: string;
//   area:string;
//   mobile: string;
//   lat: number | string;
//   long: number | string;
//   imgSrc: string;
//   categories: any[];
// };

// type Props = {
//   shops: Shop[];
//       setisColorArea:(data: boolean) => void;
//      isColorArea:boolean;
// };

// /*
// |--------------------------------------------------------------------------
// | Marker Colors
// |--------------------------------------------------------------------------
// */

// const colors = [
//   "#EF4444", // red
//   "#F97316", // orange
//   "#F59E0B", // amber
//   "#EAB308", // yellow
//   "#22C55E", // green
//   "#10B981", // emerald
//   "#06B6D4", // cyan
//   "#3B82F6", // blue
//   "#6366F1", // indigo
//   "#8B5CF6", // violet
//   "#A855F7", // purple
//   "#EC4899", // pink
//   "#F43F5E", // rose
//   "#14B8A6", // teal
//   "#84CC16", // lime
// ];

// /*
// |--------------------------------------------------------------------------
// | Generate random color for every city
// |--------------------------------------------------------------------------
// */

// const createCityColors = (shops: Shop[]) => {
//   const cityColors: Record<string, string> = {};

//   const cities = [
//     ...new Set(
//       shops
//         .map((shop) => shop.area?.trim())
//         .filter(Boolean)
//     ),
//   ];

//   // Copy colors so we can remove used colors
//   const availableColors = [...colors];

//   cities.forEach((city) => {
//     // If we have used all colors, start reusing them
//     if (availableColors.length === 0) {
//       availableColors.push(...colors);
//     }

//     const randomIndex = Math.floor(
//       Math.random() * availableColors.length
//     );

//     cityColors[city] = availableColors[randomIndex];

//     // Remove selected color so next city gets another color
//     availableColors.splice(randomIndex, 1);
//   });

//   return cityColors;
// };

// /*
// |--------------------------------------------------------------------------
// | Map Controller
// |--------------------------------------------------------------------------
// */

// const MapController = ({
//   shops,
// }: {
//   shops: Shop[];
// }) => {
//   const map = useMap();
 
//   useEffect(() => {
//     console.log(shops);
//     if (!map || shops.length === 0) return;

//    const bounds =
//   new window.google.maps.LatLngBounds();

//     shops.forEach((shop) => {
//       const lat = Number(shop.lat);
//       const lng = Number(shop.long);

//       if (
//         Number.isFinite(lat) &&
//         Number.isFinite(lng)
//       ) {
//         bounds.extend({ lat, lng });
//       }
//     });

//     map.fitBounds(bounds, 100);

//     // عند وجود نتيجة واحدة فقط
//     if (shops.length === 1) {
//       map.setZoom(15);
//     }
  
//   }, [map, shops]);

//   return null;
// };

// /*
// |--------------------------------------------------------------------------
// | Maps Component
// |--------------------------------------------------------------------------
// */

// const Maps = ({ shops,isColorArea,setisColorArea }: Props) => {
  
//   const [
//     selectedShop,
//     setSelectedShop,
//   ] = useState<Shop | null>(null);

//   /*
//   |--------------------------------------------------------------------------
//   | City Colors
//   |--------------------------------------------------------------------------
//   */

//   const [cityColors, setCityColors] =
//     useState<Record<string, string>>({});

//   /*
//   |--------------------------------------------------------------------------
//   | Update city colors when shops change
//   |--------------------------------------------------------------------------
//   */

//   useEffect(() => {
//     if (shops.length === 0) {
//       setCityColors({});
//       return;
//     }

//     const colorsForCities =
//       createCityColors(shops);

//     console.log(
//       "CITY COLORS:",
//       colorsForCities
//     );

//     setCityColors(colorsForCities);
//   }, [shops]);

//   /*
//   |--------------------------------------------------------------------------
//   | Filter valid shops
//   |--------------------------------------------------------------------------
//   */

//   const validShops = shops.filter((shop) => {
//     const lat = Number(shop.lat);
//     const lng = Number(shop.long);

//     return (
//       Number.isFinite(lat) &&
//       Number.isFinite(lng) &&
//       lat !== 0 &&
//       lng !== 0
//     );
//   });

  

//   /*
//   |--------------------------------------------------------------------------
//   | Default center - Saudi Arabia
//   |--------------------------------------------------------------------------
//   */

//   const center = {
//     lat:
//       validShops.length > 0
//         ? Number(validShops[0].lat)
//         : 24.2669,

//     lng:
//       validShops.length > 0
//         ? Number(validShops[0].long)
//         : 45.1078,
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | Render
//   |--------------------------------------------------------------------------
//   */

//   return (
//     <APIProvider
//       apiKey={
//         process.env
//           .REACT_APP_GOOGLE_MAPS_API_KEY || ""
//       }
//     >
//       <Map
//         defaultCenter={center}
//         defaultZoom={6}
//         mapId="YOUR_MAP_ID"
//         style={{
//           width: "100%",
//           height: "800px",
//         }}
//       >

//         {/* 
//           Move map when search results change
//         */}
//         <MapController
//           shops={validShops}
//         />

//         {/* 
//           Markers
//         */}
//        <ClusterMarkers
//   shops={validShops}
//   onMarkerClick={(shop) => {
//     setSelectedShop(shop);
//   }}
// />

//         {/* 
//           Info Window
//         */}
//         {selectedShop && (
//           <InfoWindow
//             position={{
//               lat: Number(
//                 selectedShop.lat
//               ),
//               lng: Number(
//                 selectedShop.long
//               ),
//             }}
//             onCloseClick={() => {
//               setSelectedShop(null);
//             }}
//           >
//             <div
//               className="p-4"
//               style={{
//                 minWidth: "250px",
//               }}
//             >

//               {/* 
//                 Header
//               */}
//               <div className="flex justify-between items-start mb-2">
//                 <div>
//                   <h3 className="font-headline-md text-label-md text-primary m-0">
//                     {selectedShop.branchName}
//                   </h3>

//                   <p className="text-label-sm text-on-surface-variant mt-1">
//                     {selectedShop.city}
//                   </p>
//                 </div>

//                 <span
//                   className="material-symbols-outlined"
//                   style={{
//                     color:
//                       cityColors[
//                         selectedShop.city?.trim()
//                       ] || "#64748B",
//                     fontSize: "20px",
//                   }}
//                 >
//                   verified
//                 </span>
//               </div>

//               {/* 
//                 Address
//               */}
//               <div className="flex items-center gap-1 text-label-sm text-on-surface-variant mb-4">
//                 <span className="material-symbols-outlined text-[14px]">
//                   location_on
//                 </span>

//                 <span>
//                   {selectedShop.address}
//                 </span>
//               </div>
 

//             </div>
//           </InfoWindow>
//         )}

//       </Map>
//     </APIProvider>
//   );
// };

// export default Maps;


